import { trackB2BLeadConversion } from '@src/telemetry/integrations/ServerSGTMPixel'

/**
 * Pages where a HubSpot form submit counts as a B2B ad conversion — the DEBI-8567 list.
 * Matching is exact, after stripping a US/CA locale prefix (`/ca/corporate-gifting`).
 */
/* eslint-disable i18next/no-literal-string -- route paths, not user-facing copy */
const B2B_CONVERSION_ROUTES = [
  '/business',
  '/corporate-gifting',
  '/business/interior-design',
  '/white-label',
  '/business/hospitality',
  '/business/branded-storefront',
  '/business/uniforms',
]

// US/CA only — the campaign's markets. UK prefixes are omitted on purpose.
const URL_LOCALE_PREFIXES = ['en-us', 'us', 'ca', 'en-ca', 'fr-ca']
/* eslint-enable i18next/no-literal-string */

const canonicalizePath = (path: string): string => {
  const trimmed = (path.split('?')[0] ?? '').replace(/^\/+/, '').replace(/\/+$/, '')
  const [firstSegment, ...rest] = trimmed.split('/')
  return firstSegment && URL_LOCALE_PREFIXES.includes(firstSegment.toLowerCase()) ? rest.join('/') : trimmed
}

/**
 * Exact match of `pathname` against an allowlist of B2B landing-page routes, after the same
 * query-string / slash / US-CA-locale-prefix canonicalisation on both sides. Shared by the
 * per-pixel allowlists (e.g. LinkedIn) so they don't each grow their own path normaliser.
 */
export const isB2BLandingPageRoute = (pathname: string | undefined | null, routes: readonly string[]): boolean => {
  const normalized = pathname ? canonicalizePath(pathname) : ''
  return Boolean(normalized) && routes.some(route => normalized === canonicalizePath(route))
}

export const isB2BConversionRoute = (pathname: string | undefined | null): boolean =>
  isB2BLandingPageRoute(pathname, B2B_CONVERSION_ROUTES)

/**
 * EBB-439 — do NOT push `trade_application_submission` to dataLayer here: GA4 already
 * emits it. FORM_SUBMIT is sent with no `category`, so `makeEventName()` in
 * `integrations/GoogleAnalytics4.ts` falls through to `action.toLowerCase()` and calls
 * `gtag('event', 'trade_application_submission', …)`, which GTM matches. Pushing it again
 * makes GTM fire the tag twice and double-counts the conversion.
 *
 * Fragile: giving FORM_SUBMIT a real `category` renames the event and would silently kill
 * the conversion — if you do that, move it to an explicit push in the same change. See
 * EBB-439 for the GTM/Google Ads configuration and incident history.
 */

/**
 * Sends the SGTM lead conversion on allowlisted routes. Emits no `LEAD` telemetry event,
 * so FbPixel and every other integration keep their current behaviour.
 */
export const trackB2BFormConversion = ({
  pathname,
  hubspotFormId,
}: {
  pathname: string
  hubspotFormId?: string
}): void => {
  if (!isB2BConversionRoute(pathname)) return

  // Hashing can reject (no `crypto.subtle` in an insecure context), and this runs inside a form
  // submit — never let it surface as an unhandled rejection.
  trackB2BLeadConversion({ contentName: pathname, hubspotFormId }).catch((error: unknown) => {
    console.error('B2B lead conversion failed', error)
  })
}
